import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { skillProgressService } from '../services/skillProgressService';
import { clubService, Club } from '../services/clubService';
import { PlayerProfile, GameRequest } from '../types/skillProgress';
import './PlayerMatchingScreen.css';

const PlayerMatchingScreen: React.FC = () => {
  const { currentSport, theme } = useTheme();
  const { user, joinClub, leaveClub } = useAuth();
  const [players, setPlayers] = useState<PlayerProfile[]>([]);
  const [gameRequests, setGameRequests] = useState<GameRequest[]>([]);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [showPlayerModal, setShowPlayerModal] = useState(false);
  const [showGameRequestModal, setShowGameRequestModal] = useState(false);
  const [showClubModal, setShowClubModal] = useState(false);
  const [showCreateClub, setShowCreateClub] = useState(false);
  const [createClubForm, setCreateClubForm] = useState({ name: '', emails: '' });
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerProfile | null>(null);
  const [selectedClub, setSelectedClub] = useState<Club | null>(null);
  const [searchFilters, setSearchFilters] = useState({
    skillLevel: '',
    location: '',
    club: '',
    sameClubOnly: false
  });

  // Game request form state
  const [gameRequestForm, setGameRequestForm] = useState({
    date: '',
    time: '',
    duration: 1,
    location: '',
    skillLevel: 'intermediate' as 'beginner' | 'intermediate' | 'advanced',
    gameType: 'doubles' as 'singles' | 'doubles' | 'mixed',
    message: ''
  });

  useEffect(() => {
    if (user) {
      loadClubs();
      loadPlayers();
      loadGameRequests();
    }
  }, [user, searchFilters]);

  const loadClubs = async () => {
    try {
      const sportClubs = await clubService.getClubsBySport(currentSport.id);
      setClubs(sportClubs);
    } catch (error) {
      console.error('Error loading clubs:', error);
    }
  };

  const loadPlayers = async () => {
    if (!user) return;

    try {
      let clubFilter = searchFilters.club;
      
      // If "same club only" is selected, filter to user's club
      if (searchFilters.sameClubOnly && user.clubs[currentSport.id]) {
        clubFilter = user.clubs[currentSport.id].clubId;
      }

      const allPlayers = await skillProgressService.searchPlayers({
        sport: currentSport.id,
        skillLevel: searchFilters.skillLevel || undefined,
        location: searchFilters.location || undefined,
        club: clubFilter || undefined,
        excludeUserId: user.id
      });
      setPlayers(allPlayers);
    } catch (error) {
      console.error('Error loading players:', error);
    }
  };

  const loadGameRequests = async () => {
    if (!user) return;

    try {
      const requests = await skillProgressService.getGameRequests(user.id);
      setGameRequests(requests);
    } catch (error) {
      console.error('Error loading game requests:', error);
    }
  };

  const handlePlayerClick = (player: PlayerProfile) => {
    setSelectedPlayer(player);
    setShowPlayerModal(true);
  };

  const handleRequestGame = () => {
    if (!selectedPlayer) return;
    
    setGameRequestForm({
      date: '',
      time: '',
      duration: 1,
      location: '',
      skillLevel: selectedPlayer.skillLevel,
      gameType: 'doubles',
      message: ''
    });
    setShowGameRequestModal(true);
  };

  const handleSendGameRequest = async () => {
    if (!user || !selectedPlayer) return;

    try {
      await skillProgressService.createGameRequest({
        requesterId: user.id,
        targetPlayerId: selectedPlayer.userId,
        sport: currentSport.id,
        date: gameRequestForm.date,
        time: gameRequestForm.time,
        duration: gameRequestForm.duration,
        location: gameRequestForm.location,
        skillLevel: gameRequestForm.skillLevel,
        gameType: gameRequestForm.gameType,
        message: gameRequestForm.message
      });

      await loadGameRequests();
      setShowGameRequestModal(false);
      setShowPlayerModal(false);
    } catch (error) {
      console.error('Error sending game request:', error);
    }
  };

  const handleGameRequestResponse = async (requestId: string, status: 'accepted' | 'declined') => {
    try {
      await skillProgressService.updateGameRequestStatus(requestId, status);
      await loadGameRequests();
    } catch (error) {
      console.error('Error responding to game request:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return '#FF9800';
      case 'accepted': return '#4CAF50';
      case 'declined': return '#F44336';
      case 'completed': return '#2196F3';
      case 'cancelled': return '#9E9E9E';
      default: return '#666';
    }
  };

  const handleJoinClub = async (clubId: string) => {
    if (!user) return;

    try {
      const club = clubs.find(c => c.id === clubId);
      if (!club) return;

      const result = await user.joinClub(currentSport.id, clubId, club.name);
      if (result.success) {
        // Add user to club members
        await clubService.addClubMember(clubId, {
          userId: user.id,
          displayName: user.name,
          ranking: 1,
          isActive: true,
          skillLevel: 'intermediate',
          playingStyle: 'all-around'
        });
        
        setShowClubModal(false);
        loadClubs();
        loadPlayers();
      } else {
        console.error('Error joining club:', result.error);
      }
    } catch (error) {
      console.error('Error joining club:', error);
    }
  };

  const handleCreateClub = async () => {
    if (!user) return;
    const name = createClubForm.name.trim() || `My ${currentSport.name} Group`;
    const emails = createClubForm.emails
      .split(/[\n,]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e && e.includes('@'));

    try {
      const clubId = await clubService.createClub({
        name,
        sport: currentSport.id,
        location: '',
        description: 'Club created by members',
        isActive: true,
        contactInfo: {},
        invitedEmails: emails.length > 0 ? emails : undefined
      });
      await clubService.addClubMember(clubId, {
        userId: user.id,
        displayName: user.name,
        ranking: 1,
        isActive: true,
        skillLevel: 'intermediate',
        playingStyle: 'all-around'
      });
      await joinClub(currentSport.id, clubId, name);
      setCreateClubForm({ name: '', emails: '' });
      setShowCreateClub(false);
      setShowClubModal(false);
      loadClubs();
      loadPlayers();
    } catch (error) {
      console.error('Error creating club:', error);
    }
  };

  const handleLeaveClub = async () => {
    if (!user || !user.clubs[currentSport.id]) return;

    try {
      const result = await leaveClub(currentSport.id);
      if (result.success) {
        // Remove user from club members
        await clubService.removeClubMember(user.clubs[currentSport.id].clubId, user.id);
        loadClubs();
        loadPlayers();
      } else {
        console.error('Error leaving club:', result.error);
      }
    } catch (error) {
      console.error('Error leaving club:', error);
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'accepted': return 'Accepted';
      case 'declined': return 'Declined';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  const isMyRequest = (request: GameRequest) => {
    return request.requesterId === user?.id;
  };

  if (!user) {
    return (
      <div className="player-matching-screen">
        <div className="container">
          <h1>Find Players</h1>
          <p>Please sign in to find and connect with other players.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="player-matching-screen">
      <div className="container">
        <div className="player-matching-header">
          <h1>Find {currentSport.name} Players</h1>
          <p>Connect with players in your area and arrange games</p>
        </div>

        {/* Club Status */}
        {user.clubs[currentSport.id] && (
          <div className="club-status" style={{ 
            backgroundColor: theme.colors.primary + '10', 
            padding: '15px', 
            borderRadius: '8px', 
            marginBottom: '20px',
            border: `1px solid ${theme.colors.primary}20`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: '0 0 5px 0', color: theme.colors.primary }}>
                  🏆 {user.clubs[currentSport.id].clubName}
                </h3>
                <p style={{ margin: '0', color: theme.colors.text }}>
                  Your Ranking: <strong>#{user.clubs[currentSport.id].ranking}</strong>
                </p>
              </div>
              <button
                onClick={handleLeaveClub}
                style={{
                  backgroundColor: theme.colors.error,
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Leave Club
              </button>
            </div>
          </div>
        )}

        {/* Search Filters */}
        <div className="search-filters">
          <div className="filter-group">
            <label className="filter-label">Skill Level:</label>
            <select
              className="filter-select"
              value={searchFilters.skillLevel}
              onChange={(e) => setSearchFilters(prev => ({ ...prev, skillLevel: e.target.value }))}
            >
              <option value="">All Levels</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Location:</label>
            <input
              type="text"
              className="filter-input"
              placeholder="City, State"
              value={searchFilters.location}
              onChange={(e) => setSearchFilters(prev => ({ ...prev, location: e.target.value }))}
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">Club:</label>
            <select
              className="filter-select"
              value={searchFilters.club}
              onChange={(e) => setSearchFilters(prev => ({ ...prev, club: e.target.value }))}
            >
              <option value="">All Clubs</option>
              {user.clubs[currentSport.id] && (
                <option value={user.clubs[currentSport.id].clubId}>
                  My Club Only
                </option>
              )}
              {clubs.map(club => (
                <option key={club.id} value={club.id}>
                  {club.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <button
              onClick={() => setShowClubModal(true)}
              style={{
                backgroundColor: theme.colors.secondary,
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {user.clubs[currentSport.id] ? 'Switch Club' : 'Join or Create Club'}
            </button>
          </div>
        </div>

        {/* Players List */}
        <div className="players-section">
          <h2>Available Players ({players.length})</h2>
          
          {players.length === 0 ? (
            <div className="no-players">
              <div className="no-players-icon">👥</div>
              <p>No players found matching your criteria</p>
              <p>Try adjusting your search filters</p>
            </div>
          ) : (
            <div className="players-grid">
              {players.map((player) => (
                <div
                  key={player.userId}
                  className="player-card"
                  onClick={() => handlePlayerClick(player)}
                >
                  <div className="player-header">
                    <div className="player-photo">
                      {player.profilePhoto || '👤'}
                    </div>
                    <div className="player-info">
                      <h3 className="player-name">{player.displayName}</h3>
                      <p className="player-location">{player.location}</p>
                      {player.club && (
                        <p className="player-club">🏢 {player.club}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="player-stats">
                    <div className="stat-item">
                      <span className="stat-label">Skill:</span>
                      <span className="stat-value">{player.skillLevel}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Style:</span>
                      <span className="stat-value">{player.playingStyle}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Matches:</span>
                      <span className="stat-value">{player.totalMatches}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Win Rate:</span>
                      <span className="stat-value">{Math.round(player.winRate)}%</span>
                    </div>
                  </div>

                  <div className="player-availability">
                    <span className="availability-label">Available:</span>
                    <div className="availability-days">
                      {player.availability.slice(0, 3).map((day, index) => (
                        <span key={index} className="availability-day">{day}</span>
                      ))}
                      {player.availability.length > 3 && (
                        <span className="availability-more">+{player.availability.length - 3}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Game Requests */}
        <div className="game-requests-section">
          <h2>Game Requests ({gameRequests.length})</h2>
          
          {gameRequests.length === 0 ? (
            <div className="no-requests">
              <div className="no-requests-icon">📅</div>
              <p>No game requests yet</p>
              <p>Find players above to start arranging games!</p>
            </div>
          ) : (
            <div className="requests-list">
              {gameRequests.map((request) => (
                <div key={request.id} className="request-card">
                  <div className="request-header">
                    <h4 className="request-title">
                      {isMyRequest(request) ? 'Your Request' : 'Game Request'}
                    </h4>
                    <span 
                      className="request-status"
                      style={{ backgroundColor: getStatusColor(request.status) }}
                    >
                      {getStatusText(request.status)}
                    </span>
                  </div>
                  
                  <div className="request-details">
                    <p className="request-detail">📅 {request.date} at {request.time}</p>
                    <p className="request-detail">⏱️ {request.duration} hour{request.duration > 1 ? 's' : ''}</p>
                    <p className="request-detail">📍 {request.location}</p>
                    <p className="request-detail">🎯 {request.skillLevel} {request.gameType}</p>
                    {request.message && (
                      <p className="request-message">💬 {request.message}</p>
                    )}
                  </div>

                  {!isMyRequest(request) && request.status === 'pending' && (
                    <div className="request-actions">
                      <button
                        className="accept-button"
                        onClick={() => handleGameRequestResponse(request.id, 'accepted')}
                      >
                        ✅ Accept
                      </button>
                      <button
                        className="decline-button"
                        onClick={() => handleGameRequestResponse(request.id, 'declined')}
                      >
                        ❌ Decline
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Player Detail Modal */}
      {showPlayerModal && selectedPlayer && (
        <div className="modal-overlay" onClick={() => setShowPlayerModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Player Profile</h3>
              <button 
                className="close-button"
                onClick={() => setShowPlayerModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="player-detail-header">
                <div className="player-detail-photo">
                  {selectedPlayer.profilePhoto || '👤'}
                </div>
                <div className="player-detail-info">
                  <h4 className="player-detail-name">{selectedPlayer.displayName}</h4>
                  <p className="player-detail-location">{selectedPlayer.location}</p>
                  {selectedPlayer.club && (
                    <p className="player-detail-club">🏢 {selectedPlayer.club}</p>
                  )}
                </div>
              </div>

              <div className="player-detail-stats">
                <div className="detail-stat">
                  <span className="detail-stat-label">Skill Level:</span>
                  <span className="detail-stat-value">{selectedPlayer.skillLevel}</span>
                </div>
                <div className="detail-stat">
                  <span className="detail-stat-label">Playing Style:</span>
                  <span className="detail-stat-value">{selectedPlayer.playingStyle}</span>
                </div>
                <div className="detail-stat">
                  <span className="detail-stat-label">Total Matches:</span>
                  <span className="detail-stat-value">{selectedPlayer.totalMatches}</span>
                </div>
                <div className="detail-stat">
                  <span className="detail-stat-label">Win Rate:</span>
                  <span className="detail-stat-value">{Math.round(selectedPlayer.winRate)}%</span>
                </div>
                <div className="detail-stat">
                  <span className="detail-stat-label">Average Rating:</span>
                  <span className="detail-stat-value">⭐ {selectedPlayer.averageRating.toFixed(1)}</span>
                </div>
              </div>

              {selectedPlayer.bio && (
                <div className="player-detail-bio">
                  <h5>About</h5>
                  <p>{selectedPlayer.bio}</p>
                </div>
              )}

              <div className="player-detail-availability">
                <h5>Availability</h5>
                <div className="availability-list">
                  {selectedPlayer.availability.map((day, index) => (
                    <span key={index} className="availability-item">{day}</span>
                  ))}
                </div>
              </div>

              <div className="player-detail-preferences">
                <h5>Preferred Times</h5>
                <div className="preferences-list">
                  {selectedPlayer.preferredTimes.map((time, index) => (
                    <span key={index} className="preference-item">{time}</span>
                  ))}
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="request-game-button"
                style={{ backgroundColor: theme.colors.primary }}
                onClick={handleRequestGame}
              >
                📅 Request Game
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Game Request Modal */}
      {showGameRequestModal && selectedPlayer && (
        <div className="modal-overlay" onClick={() => setShowGameRequestModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Request Game with {selectedPlayer.displayName}</h3>
              <button 
                className="close-button"
                onClick={() => setShowGameRequestModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="game-request-form">
                <div className="form-group">
                  <label className="form-label">Date:</label>
                  <input
                    type="date"
                    className="form-input"
                    value={gameRequestForm.date}
                    onChange={(e) => setGameRequestForm(prev => ({ ...prev, date: e.target.value }))}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Time:</label>
                  <input
                    type="time"
                    className="form-input"
                    value={gameRequestForm.time}
                    onChange={(e) => setGameRequestForm(prev => ({ ...prev, time: e.target.value }))}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Duration (hours):</label>
                  <select
                    className="form-select"
                    value={gameRequestForm.duration}
                    onChange={(e) => setGameRequestForm(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                  >
                    <option value={1}>1 hour</option>
                    <option value={1.5}>1.5 hours</option>
                    <option value={2}>2 hours</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Location:</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g., Local Pickleball Club"
                    value={gameRequestForm.location}
                    onChange={(e) => setGameRequestForm(prev => ({ ...prev, location: e.target.value }))}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Game Type:</label>
                  <select
                    className="form-select"
                    value={gameRequestForm.gameType}
                    onChange={(e) => setGameRequestForm(prev => ({ ...prev, gameType: e.target.value as any }))}
                  >
                    <option value="singles">Singles</option>
                    <option value="doubles">Doubles</option>
                    <option value="mixed">Mixed Doubles</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Message (optional):</label>
                  <textarea
                    className="form-textarea"
                    placeholder="Add a personal message..."
                    value={gameRequestForm.message}
                    onChange={(e) => setGameRequestForm(prev => ({ ...prev, message: e.target.value }))}
                    rows={3}
                  />
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="send-request-button"
                style={{ backgroundColor: theme.colors.primary }}
                onClick={handleSendGameRequest}
              >
                📤 Send Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Club Selection Modal */}
      {showClubModal && (
        <div className="modal-overlay" onClick={() => { setShowClubModal(false); setShowCreateClub(false); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{showCreateClub ? 'Create a Club' : 'Join or Create Club'}</h2>
              <button
                className="modal-close"
                onClick={() => { setShowClubModal(false); setShowCreateClub(false); }}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {showCreateClub ? (
                <div className="create-club-form">
                  <p>Create a club with just a name and member emails. You&apos;ll be added as the first member.</p>
                  <div className="form-group">
                    <label className="form-label">Club name (optional):</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder={`My ${currentSport.name} Group`}
                      value={createClubForm.name}
                      onChange={(e) => setCreateClubForm(prev => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Member emails (comma or newline separated):</label>
                    <textarea
                      className="form-textarea"
                      placeholder="friend@example.com, partner@example.com"
                      value={createClubForm.emails}
                      onChange={(e) => setCreateClubForm(prev => ({ ...prev, emails: e.target.value }))}
                      rows={3}
                    />
                  </div>
                  <div className="modal-actions" style={{ marginTop: '1rem' }}>
                    <button
                      className="send-request-button"
                      style={{ backgroundColor: theme.colors.primary }}
                      onClick={handleCreateClub}
                    >
                      Create Club
                    </button>
                    <button
                      className="form-button-secondary"
                      style={{ marginLeft: '0.5rem' }}
                      onClick={() => setShowCreateClub(false)}
                    >
                      Back
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p>Choose a club to join or create your own to find players:</p>
                  
                  <div className="clubs-list">
                    {clubs.map((club) => (
                      <div key={club.id} className="club-card">
                        <div className="club-info">
                          <h3>{club.name}</h3>
                          <p className="club-location">📍 {club.location || 'Online'}</p>
                          <p className="club-description">{club.description}</p>
                          <div className="club-stats">
                            <span className="club-member-count">
                              👥 {club.memberCount} members
                            </span>
                            <span className="club-founded">
                              📅 Founded {new Date(club.foundedAt).getFullYear()}
                            </span>
                          </div>
                        </div>
                        <button
                          className="join-club-button"
                          onClick={() => handleJoinClub(club.id)}
                          style={{
                            backgroundColor: theme.colors.primary,
                            color: 'white',
                            border: 'none',
                            padding: '10px 20px',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          Join Club
                        </button>
                      </div>
                    ))}
                  </div>

                  {clubs.length === 0 && (
                    <div className="no-clubs">
                      <p>No clubs available for {currentSport.name} yet.</p>
                      <p>Create your own club with just a name and member emails!</p>
                    </div>
                  )}

                  <div className="create-club-cta" style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--border-color, #eee)' }}>
                    <button
                      className="create-club-button"
                      onClick={() => setShowCreateClub(true)}
                      style={{
                        backgroundColor: 'transparent',
                        color: theme.colors.primary,
                        border: `2px solid ${theme.colors.primary}`,
                        padding: '10px 20px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 600
                      }}
                    >
                      + Create a Club
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerMatchingScreen;
